// Sprint AUDIT-UI — Wave 0 (#2584): frozen audit-trail response contract.
//
// Consumed by every AUDITUI story: the redaction policy (A0, #2585), the
// generalized read endpoint (A1, #2586), the `AuditTrail` component (A2,
// #2587), the entity history drawer (A3, #2588), provenance-line rollout
// (A4, #2589), and the project Activity page (A5, #2590). This is the
// durable, frozen export surface — see
// Sprint-Artefacts/BluePrint-AUDITUI-W0-Implementation-Prompt-v1_0.md §5
// before renaming or removing anything here.
//
// Field names mirror the real `audit_logs` columns verbatim, including
// `timestamp` (not `created_at` — a past production check failed on that
// assumption).
//
// Three deliberate absences, each argued and settled — do not add them
// back without re-adjudicating:
//   - No `severity` column/field. Severity is derived client-side, below.
//   - No `total`. Cursor pagination is the surviving convention.
//   - No redacted values under any key. `redactedFields` names what was
//     removed; a value reconstructable from the payload is a redaction
//     failure, not a feature.
// `AuditActor.kind` deliberately has no "agent" member (adjudication Q4)
// — agent-written rows render as system this sprint.
//
// AMENDMENT (2026-09-14, adjudication author, on the record — see
// Sprint-AUDITUI-adjudication.md's dated append block): AuditActor was
// originally a flat interface (id: string | null; displayName: string |
// null) with the null-for-system-writes invariant enforced only by
// comment. Amended to a discriminated union before this PR had any
// consumers, because a comment already failed to prevent exactly this
// shape once: `actorId: 'system'` written into a uuid column is a live
// P1 in this sprint's backlog. The union makes that shape
// unrepresentable at the type level instead of merely documented.

export type AuditScope =
  | { kind: 'entity';  entityType: string; entityId: string }
  | { kind: 'project'; projectId: string }
  | { kind: 'tenant';  tenantIds: string[]; projectIds: string[] }
  | { kind: 'all' };                       // platform admin only

export type AuditActor =
  | { kind: 'user';   id: string; displayName: string | null }
  | { kind: 'system'; id: null;   displayName: null };

export interface AuditEvent {
  id:             string;
  timestamp:      string;                  // ISO 8601 — column is `timestamp`
  action:         string;                  // free-form; may be unknown to the registry
  entityType:     string | null;
  entityId:       string | null;
  projectId:      string | null;
  actor:          AuditActor;
  reason:         string | null;           // column added in migration 0218
  before:         Record<string, unknown> | null;
  after:          Record<string, unknown> | null;
  redactedFields: string[];                // field names only, never values
}

export interface AuditPage {
  items:      AuditEvent[];
  nextCursor: string | null;               // cursor, NOT offset/page/total
}

// ── Severity classification ─────────────────────────────────────────────
//
// A pure function, exported from this same module as the contract, so the
// drawer (A3) and the Activity page (A5) cannot disagree on how an action
// is classified. There is no `severity` column — this is entirely derived.

export type AuditSeverity = 'governance' | 'routine';

// Rule 1: explicit list, the twelve governance actions among the 45 live
// (production-written) values. Matched case-insensitively for consistency
// with the pattern fallback below — 8 of 213 live action strings break the
// UPPER_SNAKE_CASE convention.
const GOVERNANCE_ACTIONS: ReadonlySet<string> = new Set([
  'GATE_OVERRIDE',
  'PROJECT_DELETED',
  'PROJECT_CREATE_ROLLED_BACK',
  'TENANT_CREATED',
  'ACADEMIC_TENANT_CREATED',
  'COHORT_TEAM_MEMBER_ASSIGNED',
  'COHORT_TEAM_MEMBER_REMOVED',
  'ESCALATION_RULES_UPDATED',
  'INFRA_CONFIG_SAVED',
  'HELP_ENABLEMENT_CHANGED',
  'ACADEMIC_TOKEN_BUDGET_CONFIG_UPDATED',
  'PROJECT_DOMAIN_PACK_CHANGED',
]);

// Rule 2: pattern fallback for the 168 values with no production rows yet.
// Case-insensitive for the same reason as above.
const GOVERNANCE_PATTERNS: readonly RegExp[] = [
  /_OVERRIDE$/i,
  /_DELETED$/i,
  /_REVOKED$/i,
  /^PLATFORM_ADMIN_/i,
  /_MEMBER_/i,
  /_ROLE_/i,
  /^TENANT_SUSPENDED(?:_|$)/i, // boundary required — R2 (codex) caught an unbounded prefix match
  /^CREDENTIAL_/i,
];

/**
 * action → severity. Rule 1 (explicit list) wins, then rule 2 (pattern
 * fallback), then rule 3 (default routine). Not a switch statement inside
 * a component — this is the one place either surface may call.
 */
export function classifyAuditSeverity(action: string): AuditSeverity {
  if (GOVERNANCE_ACTIONS.has(action.toUpperCase())) return 'governance';
  if (GOVERNANCE_PATTERNS.some((pattern) => pattern.test(action))) return 'governance';
  return 'routine';
}

// ── Sentence builders and the renderer registry ─────────────────────────
//
// AUDITUI.A2 (#2587), adjudication D1/D2/D3.
//
// D3 — these builders are PURE DATA, never JSX: actor/verb/object as three
// strings, so the same sentence can be rendered by the drawer (A3), the
// provenance line (A4) and the Activity page (A5) without any of them
// importing a component from the others, and so this module stays
// framework-free (it is also imported by apps/api).
//
// D1 — the FALLBACK IS THE PRIMARY PATH, not a degraded one. There is no
// registry of actions to be exhaustive about: the committed repo tally
// (Sprint-Artefacts/auditui-action-tally.cjs and its .json output) counts the
// writer vocabulary, which is far larger than any hand-maintained list, and
// some actions are assembled at runtime. So every row renders through
// buildFallbackSentence() unless a registered builder exists for its action.
//
// D2 — the registry SHIPS EMPTY. Bespoke per-action builders are a follow-up
// once the production frequency list is committed; picking them by guessing is
// explicitly out of scope.

/** One audit event as three display strings. No markup, no formatting. */
export interface AuditSentence {
  /** Who acted: a display name, an actor id, or 'System'. */
  actor:  string;
  /** What they did, humanized from `action`. */
  verb:   string;
  /** What it was done to, humanized from `entityType`. Null when unknown. */
  object: string | null;
}

export type AuditSentenceBuilder = (event: AuditEvent) => AuditSentence;

/**
 * Keys are UPPERCASED action strings, matching classifyAuditSeverity's
 * case-insensitive comparison — 8 live action values break the
 * UPPER_SNAKE_CASE convention, so lookup must not be case-sensitive.
 */
export type AuditRendererRegistry = ReadonlyMap<string, AuditSentenceBuilder>;

/**
 * The registry that SHIPS. Empty by D2.
 *
 * Tests MUST NOT mutate this to register a synthetic action: it is module
 * state shared across every test in the same worker, and AC6 is an assertion
 * about what ships. Pass your own Map to buildAuditSentence() instead.
 */
export const AUDIT_RENDERER_REGISTRY: AuditRendererRegistry = new Map();

/** `GATE_OVERRIDE` → `gate override`; `help_intake.created` → `help intake created`. */
function humanizeToken(raw: string): string {
  return raw
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

/**
 * The generic sentence. Correct and useful for an action no builder has ever
 * heard of, which is the normal case, not the exceptional one.
 *
 * Actor: `displayName` when the join has resolved it, else the actor id —
 * today the mapper returns displayName: null for every row
 * (auditEventMapper.ts toAuditActor), so a bare id is the honest output
 * rather than a guess. System writes carry a null actor by construction.
 */
export function buildFallbackSentence(event: AuditEvent): AuditSentence {
  const actor = event.actor.kind === 'system'
    ? 'System'
    : (event.actor.displayName ?? event.actor.id);

  return {
    actor,
    verb:   humanizeToken(event.action) || event.action,
    object: event.entityType ? humanizeToken(event.entityType) : null,
  };
}

/**
 * Resolve a builder for `event`, then build. `registry` is INJECTED (default:
 * the shipped one) so a caller — including a test — can supply its own map
 * without touching shared module state.
 */
export function buildAuditSentence(
  event: AuditEvent,
  registry: AuditRendererRegistry = AUDIT_RENDERER_REGISTRY,
): AuditSentence {
  const builder = registry.get(event.action.toUpperCase());
  return builder ? builder(event) : buildFallbackSentence(event);
}

/** Whether `action` has a registered builder in `registry`. Case-insensitive. */
export function hasRegisteredRenderer(
  action: string,
  registry: AuditRendererRegistry = AUDIT_RENDERER_REGISTRY,
): boolean {
  return registry.has(action.toUpperCase());
}
